import {
  ResultItem,
  ResultKey,
  TranscriptionFile,
  WorkRecord,
  availableResults,
  escapeHtml,
} from "./core";

export const STORAGE_KEY = "lychee-transcription-prototype-v1";

export const SAMPLE_FILES: Record<"video" | "audio" | "doc", TranscriptionFile> =
  {
    video: {
      name: "桌面收纳的三个方法.mp4",
      type: "video",
      seconds: 180,
      bytes: 25400000,
      chars: 0,
      sample: true,
    },
    audio: {
      name: "聊聊专注力：减少任务切换.mp3",
      type: "audio",
      seconds: 180,
      bytes: 4400000,
      chars: 0,
      sample: true,
    },
    doc: {
      name: "用户访谈纪要.docx",
      type: "doc",
      seconds: 0,
      bytes: 420000,
      chars: 10000,
      pages: 8,
      sample: true,
    },
  };

export const ORIGINAL_TEXTS: Record<"video" | "audio" | "doc", string> = {
  video:
    "今天分享三个整理桌面的方法。先把桌面上的东西全部拿走，按使用频率分组。每天都会用的物品放在伸手能拿到的位置，偶尔用的东西收进抽屉，不再使用的物品移出工作区。\n\n第二步是整理线材。把连接电脑、显示器和台灯的线分开，留出日常需要的长度，再用理线带固定。不要把所有线缠在一起，后续调整会更麻烦。\n\n最后，给常用物品确定位置。这里不需要添置很多收纳盒，重点是用完之后能放回原处。每天结束工作前花一点时间复位，让第二天可以直接开始。",
  audio:
    "很多人把专注理解为长时间不休息，但真正消耗注意力的，往往是频繁切换任务。写一段内容时突然回复消息，回来后又要重新找回思路。\n\n我的做法是先给今天最重要的任务留出一段完整时间。开始之前，把需要的材料准备好，把无关提醒暂时关闭。想到别的事情就先记下来，不马上切过去。\n\n休息也应该有边界。完成一个工作段后离开屏幕，走动一下，再决定接下来做什么。我们不一定能控制所有打断，但可以减少自己主动制造的切换。",
  doc: "用户访谈纪要\n\n一、访谈目标\n了解内容创作者如何从音视频素材中整理文案，以及在总结和重写过程中的困难。\n\n二、主要发现\n用户通常先听完素材，再手动记录重点。长音频中的信息定位比较费时，跨工具复制也打断了工作。部分用户只需要摘要，另一些用户希望保留完整原文核对。\n\n三、对产品的期待\n上传后可以直接选择需要的结果，无需先浏览完整原文。生成结果应保留关键信息，支持复制，并允许随时回看原文。收费应在开始处理前说明。\n\n四、后续事项\n围绕上传、结果选择、积分预估和结果阅读验证原型。当前发现不代表全部用户，仍需增加样本进一步核实。",
};

export const SUMMARIES: Record<"video" | "audio" | "doc", [string, string[]]> = {
  video: [
    "这段视频演示了三个桌面收纳方法：按使用频率分组、整理线材、为常用物品确定位置。重点在于建立便于取用和复位的习惯，而不是增加收纳用品。",
    [
      "按使用频率分组：常用物品留在手边，低频物品收进抽屉。",
      "整理线材：按设备区分，保留必要长度，便于后续调整。",
      "固定物品位置：用完归位，工作结束后整理桌面。",
    ],
  ],
  audio: [
    "这段音频讨论了频繁切换任务对注意力的影响，并提出通过提前准备材料、减少提醒和安排有边界的休息来改善专注。",
    [
      "为重要任务留出不被主动打断的时间。",
      "把临时想法先记录下来，避免马上切换任务。",
      "完成一段工作后适当休息，再安排下一步。",
    ],
  ],
  doc: [
    "访谈纪要记录了内容创作者整理音视频时的困难，以及对直接选择结果、保留原文和明确积分费用的期待，后续将通过原型验证这些方向。",
    [
      "主要困难：长素材整理费时，跨工具复制打断工作。",
      "结果需求：按需获得摘要或重写稿，同时能回看原文。",
      "体验要求：上传后直接选择结果，处理前展示费用。",
      "研究边界：发现尚需更多用户样本验证。",
    ],
  ],
};

function originalText(file: TranscriptionFile) {
  return file.text || ORIGINAL_TEXTS[file.type];
}

export function resultData(
  key: ResultKey,
  source: Pick<WorkRecord, "file" | "scenario" | "prompt">,
): ResultItem {
  const type = source.file.type;
  const noSpeech = source.scenario === "nospeech";
  let html = "";
  let plain = "";
  let basis = "基于原文";

  if (key === "original") {
    plain = originalText(source.file);
    html = plain
      .split(/\n\n/)
      .map((p) => `<p>${escapeHtml(p)}</p>`)
      .join("");
    basis = type === "doc" ? "文档正文" : "语音转录";
  }

  if (key === "visual") {
    basis = "基于画面";
    plain =
      "主体与场景\n一张靠窗的工作桌，桌面摆放电脑、台灯、线材与文具。\n\n主要过程\n演示者先移开杂物，再按使用频率分组，整理设备线材，最后将常用物品放回固定位置。\n\n重要变化\n桌面从物品分散、线材交错，变为常用物品集中、工作区域清晰。";
    html =
      "<h3>主体与场景</h3><p>一张靠窗的工作桌，桌面摆放电脑、台灯、线材与文具。</p><h3>主要过程</h3><ul><li>移开桌面物品，按用途与使用频率重新分组。</li><li>将设备线材分开、固定，整理桌面下方空间。</li><li>把电脑与常用文具放回位置，展示整理前后的变化。</li></ul><h3>重要变化</h3><p>桌面从物品分散、线材交错，变为常用物品集中、工作区域清晰。</p>";
  }

  if (key === "summary") {
    basis =
      type === "video"
        ? noSpeech
          ? "基于画面 · 未检测到语音"
          : "基于画面与语音"
        : `基于${type === "doc" ? "文档原文" : "语音原文"}`;
    const [intro, points] = SUMMARIES[type];
    plain = `内容摘要\n${intro}\n\n核心要点\n${points.map((p, i) => `${i + 1}. ${p}`).join("\n")}`;
    html = `<h3>内容摘要</h3><p>${intro}</p><h3>核心要点</h3><ul>${points.map((p) => `<li>${p}</li>`).join("")}</ul>`;
  }

  if (key === "structure") {
    const rows =
      type === "video"
        ? [
            ["开头", "提出桌面收纳问题，给出三个方法。", "说明主题，建立明确预期。"],
            ["展开", "依次介绍物品分组、线材整理、固定位置。", "将方法分解为可执行步骤。"],
            ["结尾", "强调用完复位，不必添置过多收纳用品。", "回到核心习惯，收束观点。"],
          ]
        : type === "audio"
          ? [
              ["提出问题", "频繁切换任务消耗注意力。", "澄清对专注的理解。"],
              ["方法展开", "留出完整时间，准备材料，减少提醒。", "提供具体可执行的方法。"],
              ["结尾", "安排有边界的休息，减少主动切换。", "总结核心行动。"],
            ]
          : [
              ["研究目标", "了解素材整理与再创作中的困难。", "界定访谈范围。"],
              ["发现与期待", "整理耗时、跨工具复制、按需获得结果。", "呈现问题与需求。"],
              ["后续验证", "通过原型验证，继续增加样本。", "说明下一步与证据边界。"],
            ];
    html = `<table><thead><tr><th>内容段落</th><th>主要内容</th><th>表达作用</th></tr></thead><tbody>${rows
      .map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join("")}</tr>`)
      .join("")}</tbody></table>`;
    plain = rows.map((r) => r.join("：")).join("\n");
  }

  if (key === "rewrite") {
    plain =
      type === "video"
        ? "桌面总是越收越乱？先别急着买收纳盒，试试这三个方法。\n\n先按使用频率分组：每天用的放手边，偶尔用的进抽屉，用不到的移出工作区。接着整理线材，把不同设备的线分开，留好长度再固定。最后，给每件常用物品一个固定位置，用完就归位。\n\n整理的目的，是让你更容易开始工作。每天收工前顺手复位，比反复大扫除更容易坚持。"
        : type === "audio"
          ? "总觉得忙了一天，却没推进最重要的事？可以先看看自己切换了多少次任务。\n\n开始工作前准备好材料，暂时关闭无关提醒。突然想到别的事，就先记下来，留到这段工作完成后再处理。休息时离开屏幕走一走，回来再决定下一步。\n\n不用追求一直专注，先减少自己主动制造的打断。"
          : "整理音视频素材，真正费时间的往往不只是听写，还有找到重点和反复复制。\n\n这次访谈中，用户希望上传后就能直接选择摘要、拆解或重写，同时保留原文，方便核对。处理之前还应明确展示费用。\n\n下一步，我们将用原型验证这些操作，并继续补充用户样本，确认需求是否具有普遍性。";
    html = plain
      .split("\n\n")
      .map((p) => `<p>${escapeHtml(p)}</p>`)
      .join("");
    basis = "基于原文与重写要求";
  }

  return {
    status: "success",
    html,
    plain,
    basis,
    prompt: key === "rewrite" ? source.prompt : "",
    updated: Date.now(),
  };
}

export function buildTemplatePreview(key: "video" | "audio" | "doc"): WorkRecord {
  const preview: WorkRecord = {
    workId: `template-${key}`,
    createdAt: Date.now(),
    isTemplate: true,
    templateKey: key,
    file: { ...SAMPLE_FILES[key] },
    scenario: "normal",
    prompt: "改为简洁自然的短视频口播，保留核心观点。",
    selected: availableResults(SAMPLE_FILES[key]),
    cache: { original: true, video: key === "video" },
    results: {},
    active: "summary",
    charged: 0,
    job: null,
    warnings: [],
  };
  for (const k of availableResults(preview.file)) {
    preview.results[k] = resultData(k, preview);
  }
  return preview;
}

export const PRICE_RULES_HTML = `<table><thead><tr><th>处理项</th><th>音视频</th><th>文档</th></tr></thead><tbody><tr><td>提取原文</td><td>1积分 / 3分钟</td><td>1积分 / 文件</td></tr><tr><td>视频理解</td><td>1积分 / 分钟</td><td>—</td></tr><tr><td>总结、拆解（各项）</td><td>1积分 / 5分钟</td><td>1积分 / 2,000字符</td></tr><tr><td>重写</td><td>1积分 / 3分钟</td><td>1积分 / 1,000字符</td></tr></tbody></table><p>按实际秒数或字符数累计，整次提交合计后向上取整，最低1积分。1元＝10积分。</p><p>必要的原文提取和视频理解计入预估；已有基础结果不重复收费。画面概述与视频总结共用视频分析。</p><p>例如10分钟视频全部结果为21积分，10分钟音频总结为6积分。原型不会实际扣费；失败结算仅作流程演示。</p>`;
